import { useEffect, useState } from 'react';
import type { ComponentType, ReactNode } from 'react';
import { FiFile } from 'react-icons/fi';

import type { LoadState } from '../components/workflowShared';
import type { WorkspaceScanSummary } from '../components/screens/RunScreens';
import type {
  RunIssueAcceptanceStatus,
  RunIssueViewModel
} from './RunPage';

const IconFile = FiFile as unknown as ComponentType<{ 'aria-hidden'?: boolean }>;

type IssueSeverity = 'critical' | 'high' | 'medium' | 'low' | 'informational' | 'gas' | 'other';

interface RunIssueDashboardProps {
  issues: RunIssueViewModel[];
  showBaselineCoverageNotice?: boolean;
  onShowDeepScanCoverage?: () => void;
  onIssueAcceptanceChange: (issueId: string, status: RunIssueAcceptanceStatus | null) => Promise<void> | void;
  runId: string;
  workspaceScans: WorkspaceScanSummary[];
  workspaceScansState: LoadState;
  onSelectRun: (runId: string) => void;
}

interface RunIssueViewModelEnhanced extends RunIssueViewModel {
  severity: IssueSeverity;
  cleanTitle: string;
}

type IssueAcceptanceMode = 'open' | RunIssueAcceptanceStatus;

const SEVERITY_ORDER: IssueSeverity[] = [
  'critical', 'high', 'medium', 'low', 'informational', 'gas', 'other'
];

const SEVERITY_LABELS: Record<IssueSeverity, string> = {
  critical: 'Critical',
  high: 'High',
  medium: 'Medium',
  low: 'Low',
  informational: 'Info',
  gas: 'Gas',
  other: 'Other',
};

const ACCEPTANCE_LABELS: Record<RunIssueAcceptanceStatus, string> = {
  acknowledged: 'Acknowledged',
  accepted: 'Accepted',
};

const runIssueParseSeverity = (title: string, impact: string): IssueSeverity => {
  const m = title.match(/^\[(Critical|High|Medium|Low|Informational|Info|Gas)\]/i);
  if (m) {
    const s = m[1].toLowerCase();
    return (s === 'info' ? 'informational' : s) as IssueSeverity;
  }

  const imp = impact.toLowerCase();
  if (imp.includes('critical')) return 'critical';
  if (imp.includes('high')) return 'high';
  if (imp.includes('medium')) return 'medium';
  if (imp.includes('low')) return 'low';
  if (imp.includes('info')) return 'informational';
  if (imp.includes('gas')) return 'gas';
  return 'other';
};

const runIssueMapBackendSeverity = (severity: string | undefined): IssueSeverity | null => {
  if (!severity) {
    return null;
  }

  switch (severity.toLowerCase()) {
    case 'critical': return 'critical';
    case 'high': return 'high';
    case 'medium': return 'medium';
    case 'low': return 'low';
    case 'info':
    case 'informational': return 'informational';
    case 'gas': return 'gas';
    default: return null;
  }
};

const runIssueCleanTitle = (title: string): string =>
  title
    .replace(/^\[(Critical|High|Medium|Low|Informational|Info|Gas)\]\s*/i, '')
    .replace(/^(Critical|High|Medium|Low|Informational|Info|Gas)\s*[-\u2013\u2014]\s*/i, '')
    .trim();

const runIssueEnhanceIssues = (issues: RunIssueViewModel[]): RunIssueViewModelEnhanced[] =>
  issues
    .map((issue) => ({
      ...issue,
      severity: runIssueMapBackendSeverity(issue.severity) ?? runIssueParseSeverity(issue.title, issue.impact),
      cleanTitle: runIssueCleanTitle(issue.title),
    }))
    .sort((a, b) => SEVERITY_ORDER.indexOf(a.severity) - SEVERITY_ORDER.indexOf(b.severity));

function RunMdText({ text }: { text: string }) {
  const regex = /`([^`]+)`|\*\*([^*]+)\*\*|\*([^*]+)\*/g;
  const parts: ReactNode[] = [];
  let last = 0;
  let m: RegExpExecArray | null;

  regex.lastIndex = 0;
  while ((m = regex.exec(text)) !== null) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    if (m[1] !== undefined) {
      parts.push(<code key={m.index} className="md-code">{m[1]}</code>);
    } else if (m[2] !== undefined) {
      parts.push(<strong key={m.index}>{m[2]}</strong>);
    } else if (m[3] !== undefined) {
      parts.push(<em key={m.index}>{m[3]}</em>);
    }
    last = regex.lastIndex;
  }
  if (last < text.length) parts.push(text.slice(last));

  return <>{parts}</>;
}

function RunMdBlock({ text }: { text: string }) {
  const paras = text.split(/\n\n+/).map((p) => p.trim()).filter(Boolean);

  return (
    <>
      {paras.map((para, i) => (
        <p key={i}>
          <RunMdText text={para.replace(/\n/g, ' ')} />
        </p>
      ))}
    </>
  );
}

function SeverityBadge({ severity }: { severity: IssueSeverity }) {
  return (
    <span className={`sev-badge sev-${severity}`}>
      {SEVERITY_LABELS[severity]}
    </span>
  );
}

function AcceptanceBadge({ status }: { status: RunIssueAcceptanceStatus }) {
  return (
    <span className={`acceptance-badge acceptance-${status}`}>
      {ACCEPTANCE_LABELS[status]}
    </span>
  );
}

function AcceptanceSegment({
  value,
  disabled,
  onChange,
}: {
  value: IssueAcceptanceMode;
  disabled: boolean;
  onChange: (value: IssueAcceptanceMode) => void;
}) {
  const options: { value: IssueAcceptanceMode; label: string }[] = [
    { value: 'open', label: 'Open' },
    { value: 'acknowledged', label: 'Acknowledged' },
    { value: 'accepted', label: 'Accepted' },
  ];

  return (
    <div className="issue-acceptance-segment" role="group" aria-label="Issue review status">
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          className={`issue-acceptance-option${value === option.value ? ' active' : ''}`}
          disabled={disabled}
          aria-pressed={value === option.value}
          onClick={() => onChange(option.value)}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

function RunIssueDetail({
  issue,
  isAcceptanceUpdating,
  acceptanceError,
  onAcceptanceChange,
}: {
  issue: RunIssueViewModelEnhanced;
  isAcceptanceUpdating: boolean;
  acceptanceError: string;
  onAcceptanceChange: (status: RunIssueAcceptanceStatus | null) => void;
}) {
  return (
    <div className="issue-report">
      <div className="issue-report-header">
        <div className="issue-report-statuses">
          <SeverityBadge severity={issue.severity} />
          {issue.acceptanceStatus && <AcceptanceBadge status={issue.acceptanceStatus} />}
        </div>
        <h2 className="issue-report-title">{issue.cleanTitle}</h2>
        <AcceptanceSegment
          value={issue.acceptanceStatus ?? 'open'}
          disabled={isAcceptanceUpdating}
          onChange={(status) => onAcceptanceChange(status === 'open' ? null : status)}
        />
        {acceptanceError && (
          <div className="issue-acceptance-error" role="alert">
            {acceptanceError}
          </div>
        )}
      </div>

      <hr className="issue-report-divider" />

      <div className="issue-report-section">
        <h3>Summary</h3>
        <RunMdBlock text={issue.summary} />
      </div>

      {issue.detail && (
        <div className="issue-report-section">
          <h3>Detail</h3>
          <RunMdBlock text={issue.detail} />
        </div>
      )}

      <div className="issue-report-section">
        <h3>Impact</h3>
        <RunMdBlock text={issue.impact} />
      </div>

      {issue.files.length > 0 && (
        <div className="issue-report-section">
          <h3>Affected Files</h3>
          <div className="issue-report-files">
            {issue.files.map((file) => (
              <span key={file} className="issue-report-file">
                <IconFile aria-hidden={true} />
                {file}
              </span>
            ))}
          </div>
        </div>
      )}

      {issue.sources.length > 0 && (
        <div className="issue-report-section">
          <h3>Sources</h3>
          <div className="issue-report-sources">
            {issue.sources.map((src) => (
              <span key={src} className="issue-report-source-tag">{src}</span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function RunIssueDashboard({
  issues,
  showBaselineCoverageNotice = false,
  onShowDeepScanCoverage,
  onIssueAcceptanceChange,
  runId,
  workspaceScans,
  workspaceScansState,
  onSelectRun
}: RunIssueDashboardProps) {
  const enhancedIssues = runIssueEnhanceIssues(issues);
  const [selectedId, setSelectedId] = useState<string | null>(enhancedIssues[0]?.id ?? null);
  const [acceptanceError, setAcceptanceError] = useState('');
  const [updatingAcceptanceIssueId, setUpdatingAcceptanceIssueId] = useState<string | null>(null);

  const selectedIssue = enhancedIssues.find((i) => i.id === selectedId)
    ?? enhancedIssues[0]
    ?? null;

  const handleAcceptanceChange = async (
    issue: RunIssueViewModelEnhanced,
    nextStatus: RunIssueAcceptanceStatus | null
  ) => {
    setAcceptanceError('');
    setUpdatingAcceptanceIssueId(issue.id);

    try {
      await onIssueAcceptanceChange(issue.id, nextStatus);
    } catch (error) {
      console.error('Failed to update issue status:', error);
      setAcceptanceError('Unable to update the issue status. Please try again.');
    } finally {
      setUpdatingAcceptanceIssueId(null);
    }
  };

  useEffect(() => {
    setAcceptanceError('');
  }, [selectedIssue?.id]);

  return (
    <div className="issues-screen">
      <div className="issues-columns">
        <div className="issues-list-panel">
          <div className="issues-list-header">
            <div className="issues-list-title">
              <h2>Findings</h2>
              <span className="issues-list-count">{enhancedIssues.length}</span>
              {workspaceScansState === 'loading' && workspaceScans.length === 0 && (
                <select className="run-switcher-select" aria-label="Switch run" disabled defaultValue="">
                  <option value="">Loading runs...</option>
                </select>
              )}
              {workspaceScans.length > 0 && (
                <select
                  className="run-switcher-select"
                  aria-label="Switch run"
                  value={workspaceScans.some((scan) => scan.id === runId) ? runId : workspaceScans[0].id}
                  onChange={(event) => {
                    if (event.target.value) {
                      onSelectRun(event.target.value);
                    }
                  }}
                >
                  {workspaceScans.map((scan) => (
                    <option key={scan.id} value={scan.id}>
                      {scan.target}
                    </option>
                  ))}
                </select>
              )}
            </div>
          </div>

          <div className="issues-list-scroll" role="list">
            {enhancedIssues.length === 0 ? (
              <div className="issues-list-empty">
                No issues were found in this report.
              </div>
            ) : (
              <>
                {enhancedIssues.map((issue) => (
                  <button
                    key={issue.id}
                    type="button"
                    role="listitem"
                    className={`issues-list-item${selectedIssue?.id === issue.id ? ' selected' : ''}${issue.acceptanceStatus ? ` is-${issue.acceptanceStatus}` : ''}`}
                    onClick={() => setSelectedId(issue.id)}
                  >
                    <div className="issues-list-item-top">
                      <SeverityBadge severity={issue.severity} />
                      <span className="issues-list-item-id">#{issue.id}</span>
                      {issue.acceptanceStatus && <AcceptanceBadge status={issue.acceptanceStatus} />}
                    </div>
                    <span className="issues-list-item-title">{issue.cleanTitle}</span>
                    {issue.sources.length > 0 && (
                      <span className="issues-list-item-meta">{issue.sources.join(' · ')}</span>
                    )}
                  </button>
                ))}

                {showBaselineCoverageNotice && (
                  <div className="issues-list-advanced" role="listitem">
                    <div>
                      <strong>Advanced analysis</strong>
                      <span>Some security categories were not included in this baseline.</span>
                    </div>
                    <button type="button" onClick={onShowDeepScanCoverage}>
                      See Full Pentest coverage
                      <span aria-hidden={true}>→</span>
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        <div className="issues-detail-panel">
          {selectedIssue ? (
            <div className="issues-detail-scroll">
              <RunIssueDetail
                issue={selectedIssue}
                isAcceptanceUpdating={updatingAcceptanceIssueId === selectedIssue.id}
                acceptanceError={acceptanceError}
                onAcceptanceChange={(nextStatus) => void handleAcceptanceChange(selectedIssue, nextStatus)}
              />
            </div>
          ) : (
            <div className="issues-detail-empty">
              <span>{enhancedIssues.length === 0 ? 'No issues were found.' : 'Select an issue to view details.'}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default RunIssueDashboard;
